/**
 * Mixin for fetching price-rule-evaluated prices for products, booking services, and courses.
 *
 * Usage:
 *   mixins: [priceRulePricing],
 *   // Products:  priceRulePrice(product.id)
 *   // Bookings:  priceRulePrice(serviceId, date)     — after fetchPriceRulePrice(null, serviceId, date)
 *   // Courses:   priceRulePriceForCourse(courseId, date)  — after fetchPriceRulePrice(null, null, date, courseId)
 */

const _priceCache = {};
const _pendingRequests = {};

export default {
    data() {
        return {
            priceRulePrices: {},
        };
    },
    mounted() {
        // Requires a `product` prop on the component.
        if (this.product && this.product.id && !this.product.payWhatYouWant && !this.product.price_rule) {
            this.fetchPriceRulePrice(this.product.id);
        }
    },
    computed: {
        // Requires a `product` prop on the component.
        discountedPrice() {
            if (this.product?.price_rule?.has_discount) {
                return this.product.price_rule;
            }
            const pr = this.priceRulePrice(this.product?.id);
            if (pr && pr.has_discount) {
                return pr;
            }
            return null;
        },
    },
    methods: {
        /**
         * Get cached price-rule price for a product or booking service.
         * @param {number} id - Product ID or service ID
         * @param {string} [date] - Date string (YYYY-MM-DD)
         */
        priceRulePrice(id, date) {
            const key = date ? `${id}_${date}` : String(id);
            return this.priceRulePrices[key] || null;
        },

        /**
         * Get cached price-rule price for a course.
         * @param {number} courseId - Course ID
         * @param {string} [date] - Date string (YYYY-MM-DD), should be the course start date
         */
        priceRulePriceForCourse(courseId, date) {
            const key = date ? `c_${courseId}_${date}` : `c_${courseId}`;
            return this.priceRulePrices[key] || null;
        },

        /**
         * Fetch price-rule price for a product, booking service, or course.
         * @param {number} [productId] - Product ID
         * @param {number} [rbserviceId] - Resource booking service ID
         * @param {string} [date] - Date string (YYYY-MM-DD)
         * @param {number} [courseId] - Course ID
         */
        fetchPriceRulePrice(productId, rbserviceId, date, courseId) {
            if (!this.$api || !this.$api.hasFlag || !this.$api.hasFlag('flag-price-rule-engine')) {
                return;
            }

            let cacheKey;
            if (courseId) {
                cacheKey = date ? `c_${courseId}_${date}` : `c_${courseId}`;
            } else {
                const id = productId || rbserviceId;
                cacheKey = date ? `${id}_${date}` : String(id);
            }

            if (_priceCache[cacheKey]) {
                this.$set(this.priceRulePrices, cacheKey, _priceCache[cacheKey]);
                return;
            }
            if (_pendingRequests[cacheKey]) {
                return;
            }

            _pendingRequests[cacheKey] = true;
            const params = {};
            if (courseId) {
                params.course_id = courseId;
            } else if (rbserviceId) {
                params.rbservice_id = rbserviceId;
            } else if (productId) {
                params.product_id = productId;
            }
            if (date) {
                params.date = date;
            }

            this.$api.get('/api/public/pricerule/product_price', params).then(result => {
                delete _pendingRequests[cacheKey];
                if (result && result.has_discount) {
                    _priceCache[cacheKey] = result;
                    this.$set(this.priceRulePrices, cacheKey, result);
                } else {
                    _priceCache[cacheKey] = { has_discount: false };
                    this.$set(this.priceRulePrices, cacheKey, { has_discount: false });
                }
            }).catch(err => {
                delete _pendingRequests[cacheKey];
                console.warn('Price rule price fetch failed:', params, err);
            });
        },

        /**
         * Fetch prices for a list of products.
         * @param {Array} products - Array of objects with id property
         */
        fetchPriceRulePricesForProducts(products) {
            if (!products || !products.length) return;
            products.forEach(p => {
                if (p && p.id) {
                    this.fetchPriceRulePrice(p.id);
                }
            });
        },
    },
};
